/** Normalize GetCustomerTitleInfooneLead / Black Knight owner payloads into a flat owner list. */

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

/** True only for an explicit Black Knight synced flag; null/false/"false" mean run PropertySearch. */
export function isBlackKnightSyncedResponse(value: unknown, depth = 0): boolean {
    if (value === true || value === 1) return true;
    if (value == null || value === false || value === 0) return false;
    if (typeof value === 'string') {
        const text = value.trim().toLowerCase();
        if (text === 'true' || text === '1' || text === 'yes') return true;
        return false;
    }
    if (depth > 3) return false;
    const rec = asRecord(value);
    if (!rec) return false;
    if ('data' in rec) return isBlackKnightSyncedResponse(rec.data, depth + 1);
    if ('isSynced' in rec) return isBlackKnightSyncedResponse(rec.isSynced, depth + 1);
    if ('IsSynced' in rec) return isBlackKnightSyncedResponse(rec.IsSynced, depth + 1);
    if ('blackKnightSynced' in rec) return isBlackKnightSyncedResponse(rec.blackKnightSynced, depth + 1);
    return false;
}

export function isTitleOwnerRecord(value: unknown): boolean {
    const rec = asRecord(value);
    if (!rec) return false;
    return Boolean(
        rec.FirstName ||
            rec.firstName ||
            rec.LastName ||
            rec.lastName ||
            rec.Owner ||
            rec.owner ||
            rec.ownerName ||
            rec.OwnerName,
    );
}

function ownersFromRecord(rec: Record<string, unknown>): unknown[] | null {
    const owners = rec.owners ?? rec.Owners;
    return Array.isArray(owners) ? owners : null;
}

export function extractTitleSearchOwners(raw: unknown, depth = 0): unknown[] {
    if (raw == null || depth > 4) return [];

    if (Array.isArray(raw)) {
        if (raw.length === 0) return [];
        const nested = raw.flatMap((item) => {
            const rec = asRecord(item);
            const owners = rec ? ownersFromRecord(rec) : null;
            if (owners) return owners;
            return isTitleOwnerRecord(item) ? [item] : [];
        });
        return nested.filter(isTitleOwnerRecord);
    }

    const rec = asRecord(raw);
    if (!rec) return [];

    const directOwners = ownersFromRecord(rec);
    if (directOwners) return directOwners.filter(isTitleOwnerRecord);

    const locations = rec.locations ?? rec.Locations;
    if (Array.isArray(locations)) {
        const fromLocations = extractTitleSearchOwners(locations, depth + 1);
        if (fromLocations.length > 0) return fromLocations;
    }

    if (rec.data != null && rec.data !== raw) {
        const fromData = extractTitleSearchOwners(rec.data, depth + 1);
        if (fromData.length > 0) return fromData;
    }

    return isTitleOwnerRecord(rec) ? [rec] : [];
}

const STATE_NAME_TO_ABBREV: Record<string, string> = {
    connecticut: 'CT',
    massachusetts: 'MA',
    'new york': 'NY',
    'new jersey': 'NJ',
    'rhode island': 'RI',
    maine: 'ME',
    'new hampshire': 'NH',
    vermont: 'VT',
    pennsylvania: 'PA',
};

/** ASP.NET binds UseCode as int; empty string/null cause 400 `$[0].UseCode`. */
export function toTitleUseCode(value: unknown): number {
    if (value == null || value === '') return 0;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const text = String(value).trim();
    if (!text) return 0;
    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : 0;
}

export function toTitleStateCode(value: unknown): string {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '';
    if (trimmed.length === 2) return trimmed.toUpperCase();
    return STATE_NAME_TO_ABBREV[trimmed.toLowerCase()] ?? trimmed;
}

function normalizePersonName(value: unknown): string {
    return String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
}

/** Numeric Trinity contact id expected by OpportunityContactTitleUpdate. */
export function opportunityContactNumericId(customer: unknown): number | null {
    const rec = asRecord(customer);
    if (!rec) return null;
    const raw =
        rec.OpportunityContactId ??
        rec.opportunityContactId ??
        rec.CustomerId ??
        rec.customerId;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function matchTitleOwnerContactId(
    owner: { firstName?: string; lastName?: string; ownerName?: string },
    customers: unknown[],
): number | null {
    const first = normalizePersonName(owner.firstName);
    const last = normalizePersonName(owner.lastName);
    if (!first && !last) return null;

    const matches = customers.filter((customer) => {
        const rec = asRecord(customer);
        if (!rec) return false;
        return (
            normalizePersonName(rec.FirstName ?? rec.firstName) === first &&
            normalizePersonName(rec.LastName ?? rec.lastName) === last
        );
    });
    if (matches.length === 1) return opportunityContactNumericId(matches[0]);
    return null;
}

export function normalizeTitleContactUpdate(contact: Record<string, unknown> | null | undefined): Record<string, unknown> {
    const row = contact ?? {};
    const isUpdate = Boolean(row.IsUpdate);
    const contactId = opportunityContactNumericId(row);
    const payload: Record<string, unknown> = {
        FirstName: String(row.FirstName ?? row.firstName ?? ''),
        LastName: String(row.LastName ?? row.lastName ?? ''),
        Address: String(row.Address ?? row.street ?? ''),
        City: String(row.City ?? row.city ?? ''),
        State: toTitleStateCode(row.State ?? row.state),
        ZIP: String(row.ZIP ?? row.zipCode ?? ''),
        titleVerified: row.titleVerified !== false,
        UseCode: toTitleUseCode(row.UseCode ?? row.useCode),
        UseCodeDescription: String(row.UseCodeDescription ?? row.useCodeDescription ?? ''),
        Owner: String(row.Owner ?? row.ownerName ?? ''),
        IsUpdate: isUpdate,
    };
    if (isUpdate && contactId != null) {
        payload.OpportunityContactId = contactId;
    }
    return payload;
}

export function aspNetValidationMessage(error: unknown, fallback: string): string {
    const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
    if (!data || typeof data !== 'object') {
        const message = (error as { message?: string })?.message;
        return message?.trim() || fallback;
    }
    const errors = data.errors;
    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        const parts = Object.entries(errors as Record<string, unknown>).flatMap(([key, value]) => {
            const list = Array.isArray(value) ? value : [value];
            return list
                .map((item) => String(item ?? '').trim())
                .filter(Boolean)
                .map((item) => (key.startsWith('$') ? item : `${key}: ${item}`));
        });
        if (parts.length > 0) return parts.join(' ');
    }
    const title = String(data.title ?? data.message ?? '').trim();
    return title || fallback;
}
